import { reviewConcentration, type ReviewConcentration, type TeamStoryInput } from "./story";
import type { PeriodMeta, RosterPerson, TeamTotals } from "./types";
import { fmtNum } from "~/lib/format";

export interface PageModel {
  /** Active this period: the roster table's default rows. */
  active: RosterPerson[];
  /** Active last period only. */
  wentQuiet: RosterPerson[];
  /** Active this period, not last. */
  newThisPeriod: RosterPerson[];
  concentration: ReviewConcentration;
  prevConcentration: ReviewConcentration;
  story: TeamStoryInput;
  unlinkedCount: number;
}

/**
 * Splits the roster by period and derives what the headline needs. Nobody
 * is dropped for low volume: if the server says someone did engineering
 * work in the period, they are in `active`.
 */
export function buildPageModel(
  items: RosterPerson[],
  period: Pick<PeriodMeta, "label" | "prevLabel">,
  team: { current: TeamTotals; previous: TeamTotals } | undefined,
): PageModel {
  const active = items.filter((p) => p.activeThisPeriod);
  const wentQuiet = items.filter((p) => !p.activeThisPeriod && p.activeLastPeriod);
  const newThisPeriod = active.filter((p) => !p.activeLastPeriod);
  const concentration = reviewConcentration(active);
  const prevConcentration = reviewConcentration(
    items
      .filter((p) => p.previous)
      .map((p) => ({ id: p.id, reviewsGiven: p.previous!.reviewsGiven })),
  );

  const sum = (f: (p: RosterPerson) => number) => active.reduce((s, p) => s + f(p), 0);
  const prevSum = (f: (p: NonNullable<RosterPerson["previous"]>) => number) =>
    items.reduce((s, p) => s + (p.previous ? f(p.previous) : 0), 0);

  const codeActive = active.filter((p) => p.activity.code).length;
  const prevCodeActive = items.filter(
    (p) =>
      p.previous &&
      (p.previous.prsCreated > 0 ||
        p.previous.prsMerged > 0 ||
        p.previous.reviewsGiven > 0 ||
        p.previous.totalCommits > 0),
  ).length;

  return {
    active,
    wentQuiet,
    newThisPeriod,
    concentration,
    prevConcentration,
    unlinkedCount: active.filter((p) => !p.isLinkedProfile).length,
    story: {
      periodLabel: period.label,
      prevLabel: period.prevLabel,
      prsMerged: team?.current.prsMerged ?? sum((p) => p.prsMerged),
      prevPrsMerged: team?.previous.prsMerged ?? prevSum((p) => p.prsMerged),
      activePeople: codeActive,
      prevActivePeople: prevCodeActive,
      medianHoursToMerge: team?.current.medianHoursToMerge ?? null,
      prevMedianHoursToMerge: team?.previous.medianHoursToMerge ?? null,
      concentration,
    },
  };
}

/**
 * Who to color in the throughput figure: the largest gain and the largest
 * drop, each only if it moved by at least two PRs. People new this period
 * count as rising from zero.
 */
export function biggestMovers(people: RosterPerson[]): {
  gain: RosterPerson | null;
  drop: RosterPerson | null;
} {
  let gain: RosterPerson | null = null;
  let drop: RosterPerson | null = null;
  let best = 1;
  let worst = -1;
  for (const p of people) {
    // Someone new this period moved up from zero
    const d = p.prsMerged - (p.previous?.prsMerged ?? 0);
    if (d > best) {
      best = d;
      gain = p;
    }
    if (d < worst) {
      worst = d;
      drop = p;
    }
  }
  return { gain, drop };
}

export function pointsText(n: number): string {
  return fmtNum(Math.round(n * 10) / 10);
}
